<template>
  <div>
    <div class="row">
      <div class="col">
        <div class="d-flex justify-content-between align-items-end">
          <div class="">
            <div
              class=""
              style="margin-bottom: 12px"
            >
              <b-link
                href="/vendors/"
                class="text-decoration-none"
              >
                &larr; {{ $splTrans('Vendor List') }}
              </b-link>
            </div>
            <div>
              <transition
                name="fade-fast"
                mode="out-in"
              >
                <div class="d-flex align-items-center gap-4">
                  <h1
                    class="h2 d-inline-block mb-0"
                    :key="resource.name"
                  >
                    {{ resource.name }}
                  </h1>

                  <template v-if="dynamicTaggingEnabled">
                    <TagFieldLocationDisplay
                      v-model="resource.taggings_by_field"
                      location-slug="vendors.company.header"
                    ></TagFieldLocationDisplay>
                  </template>
                  <template>
                    <featured-vendor-tag-group
                      :tags="resource.company_tags"
                      tagClass="font-size-90 text-uppercase font-weight-500"
                    ></featured-vendor-tag-group>
                  </template>
                </div>
              </transition>
            </div>
            <div class="text-muted mt-1 mb-0 font-size-90 d-flex align-items-center">
              <div>
                <b-icon
                  icon="people"
                  class="text-primary-lighter"
                ></b-icon>
                <span class="ml-2">{{ resource.active_contact_count }} {{ resource.active_contact_count == 1 ? 'contact' : 'contacts' }}</span>
              </div>
              <div
                v-if="resource.primary_location_string || resource.primary_location_country_code"
                class="d-flex align-items-center"
              >
                <span class="mx-2">&middot;</span>
                <b-icon
                  icon="geo-alt-fill"
                  class="text-primary-lighter"
                ></b-icon>
                <span class="mx-2">{{ resource.primary_location_string }}</span>
                <country-flag
                  :country="resource.primary_location_country_code"
                  size="sm"
                ></country-flag>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-nav class="spl-page-nav spl-page-nav-tight mt-3">
      <b-nav-item
        @click="onNavigate('summary')"
        :active="pageCurrent === 'summary'"
      >
        <b-icon icon="card-list"></b-icon>
        {{ $splTrans('Summary') }}
      </b-nav-item>
      <b-nav-item
        v-if="tenantInfo.custom_flags.show_company_locations_grid && !usesVendorSites"
        @click="onNavigate('locations')"
        :active="pageCurrent === 'locations'"
      >
        <b-icon icon="geo-alt-fill"></b-icon>
        {{ $splTrans('Locations') }}
      </b-nav-item>
      <b-nav-item
        v-if="usesVendorSites"
        @click="onNavigate('sites')"
        :active="pageCurrent === 'sites'"
      >
        <b-icon icon="geo-alt-fill"></b-icon>
        {{ $t('vendorHeader.sites') }}
      </b-nav-item>
      <b-nav-item
        @click="onNavigate('contacts')"
        :active="pageCurrent === 'contacts'"
      >
        <b-icon icon="people"></b-icon>
        {{ $splTrans('Contacts') }}
        <b-badge
          variant="soft-primary"
          class="ml-2"
        >
          {{ resource.active_contact_count }}
        </b-badge>
      </b-nav-item>
      <b-nav-item
        @click="onNavigate('sourcing')"
        :active="pageCurrent === 'sourcing'"
      >
        <b-icon icon="card-checklist"></b-icon>
        {{ $splTrans('Sourcing') }}
      </b-nav-item>
      <b-nav-item
        v-if="tenantInfo.custom_flags.show_new_quality"
        @click="onNavigate('quality')"
        :active="pageCurrent === 'quality'"
      >
        <b-icon icon="patch-check"></b-icon>
        {{ $splTrans('Quality Issues') }}
      </b-nav-item>
      <b-nav-item
        v-if="tenantInfo.custom_flags.show_scorecards"
        @click="onNavigate('quality_scorecards')"
        :active="pageCurrent === 'quality_scorecards'"
      >
        <!-- TODO - need to update this to allow NOT showing 'old' scorecards but showing new quality list instead -->
        <b-icon icon="speedometer2"></b-icon>
        {{ $splTrans('Quality Scorecards') }}
      </b-nav-item>
      <b-nav-item
        v-if="$splEnvIsDev"
        @click="onNavigate('performance_demo')"
        :active="pageCurrent === 'performance_demo'"
      >
        <!-- FOR DEMO PURPOSES -->
        <b-icon icon="bar-chart-line-fill"></b-icon>
        KPIs
      </b-nav-item>
      <b-nav-item
        v-if="tenantInfo.custom_flags.show_performance"
        @click="onNavigate('performance')"
        :active="pageCurrent === 'performance'"
      >
        <b-icon icon="speedometer"></b-icon>
        {{ $splTrans('Performance') }}
      </b-nav-item>
      <!-- <b-nav-item
        v-if="$splEnvIsDev && tenantInfo.custom_flags.show_compliance"
        @click="onNavigate('compliance')"
        :active="pageCurrent === 'compliance'"
      >
        <b-icon icon="check2-square"></b-icon>
        Compliance
      </b-nav-item> -->
      <b-nav-item
        v-if="tenantInfo.custom_flags.show_vendor_docs"
        @click="onNavigate('documents')"
        :active="pageCurrent === 'documents'"
      >
        <b-icon icon="file-earmark-medical"></b-icon>
        {{ $splTrans('Documents') }}
      </b-nav-item>
      <b-nav-item
        @click="onNavigate('files')"
        :active="pageCurrent === 'files'"
      >
        <b-icon icon="paperclip"></b-icon>
        {{ $splTrans('Files') }}
      </b-nav-item>
      <b-nav-item
        @click="onNavigate('notes')"
        :active="pageCurrent === 'notes'"
      >
        <b-icon icon="file-earmark-text"></b-icon>
        {{ $splTrans('Notes') }}
      </b-nav-item>
    </b-nav>
  </div>
</template>

<script>
  import { tenantInfoMixin } from '@/js/custom-vue-mixins';
  import TagFieldLocationDisplay from '@/components/taxonomy/taggings/TagFieldLocationDisplay.vue';
  import FeaturedVendorTagGroup from './FeaturedVendorTagGroup.vue';

  export default {
    mixins: [tenantInfoMixin],
    props: ['resource', 'pageActive'],
    components: {
      FeaturedVendorTagGroup,
      TagFieldLocationDisplay,
    },
    i18n: {
      messages: {
        en: { vendorHeader: { sites: 'Sites' } },
        es: { vendorHeader: { sites: 'Sitios' } },
        pt: { vendorHeader: { sites: 'Unidades' } },
        'zh-Hans': { vendorHeader: { sites: '站点' } },
        'zh-Hant': { vendorHeader: { sites: '據點' } },
      },
    },
    data() {
      return {
        pageCurrent: this.pageActive,
        // Hash routing is disabled for now
        // pageCurrent: window.location.hash.substring(1) || this.pageActive || 'summary',
      };
    },
    // watch: {
    //   pageCurrent(newPage) {
    //     window.location.hash = newPage;
    //   },
    // },
    computed: {
      dynamicTaggingEnabled() {
        return this.$getTenantFlag('taxonomy_vendor_tags', false);
      },
      usesVendorSites() {
        // tenants that use CompanySite (Sites tab) instead of CompanyLocation
        return this.$getTenantFlag('use_vendor_sites', false);
      },
    },
    methods: {
      onNavigate(payload) {
        this.$emit('navigate', payload);
        this.pageCurrent = payload;
      },
    },
    // created() {
    //   this.$emit('navigate', this.pageCurrent);
    // },
  };
</script>
